// Underwater Treasure Dive bonus round: swim DEEPER into the ocean collecting
// córdoba coins, dodging fish (a harmless bump that only wobbles you), and reach
// the deep treasure chest at the bottom to win a gold NicaBros trophy.
// Free 4-direction swim. A can't-lose descent — only the coin count varies.
import { GAME } from "./config";
import { ready } from "./assets";

const { WIDTH, HEIGHT } = GAME;
const STEP_MS = 1000 / 60; // fixed simulation timestep (frame-rate independent movement)
const TOTAL_COINS = 80;
const COURSE_LEN = 4600;          // depth units from surface (0) to seabed
const CHEST_DEPTH = COURSE_LEN - 120;
const PLAYER_BAND = HEIGHT * 0.42; // keep player around this screen line
const FISH_SPRITES = ["fish_a", "fish_b", "fish_c"];

export default class OceanDive {
  constructor(canvas, { character, assets, audio, callbacks }) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.ctx.imageSmoothingEnabled = true;
    this.char = character;
    this.assets = assets || {};
    this.audio = audio || null;
    this.cb = callbacks || {};

    this.running = false;
    this.initState();
    this.buildCourse();
    this.bindKeys();
  }

  initState() {
    this.input = { left: false, right: false, up: false, down: false };
    this.ended = false;
    this.paused = true;
    this.frame = 0;
    this.shake = 0;
    this.wobble = 0;

    this.px = WIDTH / 2;
    this.depth = 0;
    this.vx = 0;
    this.vy = 0;
    this.facing = 1;          // 1 = right, -1 = left
    this.collected = 0;
    this.hits = 0;
    this.maxHits = 3;
    this.invuln = 0;          // brief mercy window after a hit / on resume
    this.cameraDepth = 0;
    this.bubbles = [];
    this.sparks = [];
    this.tx = 0; this.ty = 0;          // analog touch axes (-1..1)
    this.txActive = false; this.tyActive = false;
    for (let i = 0; i < 26; i++) this.bubbles.push(this.newBubble(Math.random() * HEIGHT));
  }

  reset() { this.initState(); this.buildCourse(); }

  // Resume the SAME run after being knocked out: keep depth & collected coins,
  // clear the hit counter and grant a short mercy window. Waits for a countdown.
  continueDive() {
    this.ended = false;
    this.hits = 0;
    this.invuln = 120;
    this.vx = 0;
    this.vy = 0;
    this.tx = 0; this.ty = 0;
    this.txActive = false; this.tyActive = false;
    this.paused = true;
  }

  begin() {
    this.paused = false;
    this.audio?.resume();
    this.audio?.startMusic(4);
  }

  newBubble(y) {
    return { x: Math.random() * WIDTH, y: y ?? HEIGHT + 10, r: 2 + Math.random() * 5, sp: 0.6 + Math.random() * 1.4, drift: (Math.random() - 0.5) * 0.6 };
  }

  buildCourse() {
    // Córdoba coins weave across the water so the player must steer to grab them.
    this.coins = [];
    const startD = 320;
    const span = CHEST_DEPTH - 360 - startD;
    for (let i = 0; i < TOTAL_COINS; i++) {
      const d = startD + (i / (TOTAL_COINS - 1)) * span;
      const t = i * 0.5;
      const x = WIDTH / 2 + Math.sin(t * 0.55) * 300 + Math.sin(t * 0.19) * 110;
      this.coins.push({ x: Math.max(56, Math.min(WIDTH - 56, x)), d, taken: false });
    }
    // Fish patrol horizontally; bumping one only wobbles you (no coin loss).
    this.fish = [];
    const fishCount = 22;
    for (let i = 0; i < fishCount; i++) {
      const d = 560 + i * ((COURSE_LEN - 1100) / fishCount) + (Math.random() - 0.5) * 130;
      this.fish.push({
        x: 80 + Math.random() * (WIDTH - 160), d,
        dir: Math.random() < 0.5 ? 1 : -1,
        sp: 0.9 + Math.random() * 1.9,
        bob: Math.random() * Math.PI * 2,
        sprite: FISH_SPRITES[i % FISH_SPRITES.length],
        scale: 0.85 + Math.random() * 0.5,
        cool: 0,
      });
    }
    this.chest = { x: WIDTH / 2, d: CHEST_DEPTH };
  }

  bindKeys() {
    this._onKey = (e, down) => {
      const c = e.code;
      if (c === "ArrowLeft" || c === "KeyA") this.input.left = down;
      else if (c === "ArrowRight" || c === "KeyD") this.input.right = down;
      else if (c === "ArrowUp" || c === "KeyW") this.input.up = down;
      else if (c === "ArrowDown" || c === "KeyS") this.input.down = down;
      else return;
      e.preventDefault();
    };
    this._kd = (e) => this._onKey(e, true);
    this._ku = (e) => this._onKey(e, false);
    window.addEventListener("keydown", this._kd);
    window.addEventListener("keyup", this._ku);
  }
  setInput(name, val) { if (name in this.input) this.input[name] = val; }
  // Analog thumbstick input. value is -1..1, or null to release.
  setAxis(which, value) {
    if (which === "x") { this.txActive = value !== null; this.tx = value || 0; }
    else { this.tyActive = value !== null; this.ty = value || 0; }
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);
  }
  destroy() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.audio?.stopMusic();
    window.removeEventListener("keydown", this._kd);
    window.removeEventListener("keyup", this._ku);
  }
  loop() {
    if (!this.running) return;
    const now = performance.now();
    if (!this.ended && !this.paused) {
      // Fixed-timestep accumulator: keeps game speed consistent regardless of
      // device frame rate (slow phones no longer run in slow-motion).
      let frame = this._lastTs != null ? now - this._lastTs : STEP_MS;
      if (frame > 250) frame = 250; // avoid spiral-of-death after tab sleep
      this._acc = (this._acc || 0) + frame;
      let steps = 0;
      while (this._acc >= STEP_MS && steps < 5) { this.update(); this._acc -= STEP_MS; steps++; }
    }
    this._lastTs = now;
    this.render();
    this.raf = requestAnimationFrame(this.loop);
  }

  finish(completed) {
    if (this.ended) return;
    this.ended = true;
    this.audio?.stopMusic();
    this.audio?.sfx(completed ? "win" : "hurt");
    this.cb.onComplete && this.cb.onComplete({ collected: this.collected, total: TOTAL_COINS, completed, hits: this.hits });
  }

  update() {
    this.frame++;
    if (this.shake > 0) this.shake--;
    if (this.wobble > 0) this.wobble--;
    if (this.invuln > 0) this.invuln--;

    const a = this.wobble > 0 ? 0.22 : 0.62;
    const kx = (this.input.right ? 1 : 0) - (this.input.left ? 1 : 0);
    const ky = (this.input.down ? 1 : 0) - (this.input.up ? 1 : 0);
    const ix = this.txActive ? this.tx : kx; // analog stick overrides keyboard
    const iy = this.tyActive ? this.ty : ky;
    this.vx += a * ix;
    this.vy += a * iy;
    this.vy += 0.05; // gentle buoyancy/sink to keep drifting deeper

    // water drag
    this.vx *= 0.86;
    this.vy *= 0.9;
    const MAX = 6.2;
    this.vx = Math.max(-MAX, Math.min(MAX, this.vx));
    this.vy = Math.max(-MAX, Math.min(MAX, this.vy));

    this.px += this.vx;
    this.depth += this.vy;
    this.px = Math.max(40, Math.min(WIDTH - 40, this.px));
    this.depth = Math.max(0, Math.min(COURSE_LEN, this.depth));
    if (Math.abs(this.vx) > 0.4) this.facing = this.vx > 0 ? 1 : -1;

    this.cameraDepth = Math.max(0, Math.min(COURSE_LEN - HEIGHT, this.depth - PLAYER_BAND));

    // bubbles rise
    for (const b of this.bubbles) {
      b.y -= b.sp; b.x += b.drift;
      if (b.y < -10) Object.assign(b, this.newBubble());
    }
    for (const s of this.sparks) { s.x += s.vx; s.y += s.vy; s.vy += 0.12; s.life--; }
    this.sparks = this.sparks.filter((s) => s.life > 0);

    // fish movement
    for (const f of this.fish) {
      f.x += f.dir * f.sp;
      if (f.x < 60) { f.x = 60; f.dir = 1; }
      else if (f.x > WIDTH - 60) { f.x = WIDTH - 60; f.dir = -1; }
      f.bob += 0.05;
      if (f.cool > 0) f.cool--;
    }

    // coin collection
    for (const c of this.coins) {
      if (c.taken) continue;
      if (Math.abs(c.d - this.depth) < 30 && Math.abs(c.x - this.px) < 36) {
        c.taken = true; this.collected++; this.audio?.sfx("coin");
        for (let k = 0; k < 8; k++) {
          const ang = Math.random() * Math.PI * 2, sp = 1 + Math.random() * 3;
          this.sparks.push({ x: c.x, y: this.screenY(c.d), vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp - 1.5, life: 24, color: "#ffd24a" });
        }
      }
    }

    // fish bump — 3 hits knocks you out (you get one more try to continue)
    for (const f of this.fish) {
      if (f.cool > 0) continue;
      if (Math.abs(f.d - this.depth) < 30 && Math.abs(f.x - this.px) < 40) {
        f.cool = 60; this.shake = 9;
        this.vx += (this.px < f.x ? -2.4 : 2.4);
        if (this.invuln <= 0) {
          this.hits++; this.wobble = 36; this.invuln = 80; this.audio?.sfx("hurt");
          if (this.hits >= this.maxHits) { this.finish(false); return; }
        }
      }
    }

    this.cb.onProgress && this.cb.onProgress({ collected: this.collected, total: TOTAL_COINS, hits: this.hits, maxHits: this.maxHits, pct: Math.min(100, (this.depth / CHEST_DEPTH) * 100) });

    if (this.depth >= CHEST_DEPTH) this.finish(true);
  }

  screenY(d) { return d - this.cameraDepth; }

  // ---------------- Render ----------------
  render() {
    const ctx = this.ctx;
    ctx.save();
    if (this.shake > 0) ctx.translate((Math.random() - 0.5) * this.shake, (Math.random() - 0.5) * this.shake);

    this.drawWater();
    this.drawLightRays();
    this.drawSeabed();

    // chest
    const cy = this.screenY(this.chest.d);
    if (cy > -160 && cy < HEIGHT + 160) this.drawChest(this.chest.x, cy);

    // coins
    for (const c of this.coins) {
      if (c.taken) continue;
      const y = this.screenY(c.d);
      if (y < -30 || y > HEIGHT + 30) continue;
      this.drawCoin(c.x, y);
    }

    // fish
    for (const f of this.fish) {
      const y = this.screenY(f.d) + Math.sin(f.bob) * 6;
      if (y < -60 || y > HEIGHT + 60) continue;
      this.drawFish(f, y);
    }

    // bubbles
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    for (const b of this.bubbles) {
      ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2); ctx.fill();
    }

    this.drawPlayer();

    // sparks
    for (const s of this.sparks) {
      ctx.globalAlpha = Math.min(1, s.life / 20); ctx.fillStyle = s.color;
      ctx.fillRect(Math.round(s.x), Math.round(s.y), 4, 4); ctx.globalAlpha = 1;
    }

    ctx.restore();
  }

  drawWater() {
    const ctx = this.ctx;
    // Depth-tinted gradient: bright turquoise near the surface, deep navy below.
    const topFrac = Math.min(1, this.cameraDepth / COURSE_LEN);
    const botFrac = Math.min(1, (this.cameraDepth + HEIGHT) / COURSE_LEN);
    const col = (f) => {
      const r = Math.round(26 + (6 - 26) * f);
      const g = Math.round(170 + (28 - 170) * f);
      const b = Math.round(200 + (70 - 200) * f);
      return `rgb(${r},${g},${b})`;
    };
    const grd = ctx.createLinearGradient(0, 0, 0, HEIGHT);
    grd.addColorStop(0, col(topFrac));
    grd.addColorStop(1, col(botFrac));
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    // surface waterline + sun shimmer (only near the very top of the course)
    const surfY = this.screenY(0);
    if (surfY > -40 && surfY < HEIGHT) {
      const sg = ctx.createLinearGradient(0, surfY - 60, 0, surfY + 30);
      sg.addColorStop(0, "rgba(255,255,255,0.55)");
      sg.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = sg; ctx.fillRect(0, surfY - 60, WIDTH, 90);
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.fillRect(0, surfY, WIDTH, 3);
    }
  }

  drawLightRays() {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    const fade = Math.max(0, 1 - this.cameraDepth / (COURSE_LEN * 0.7));
    ctx.globalAlpha = 0.10 * fade;
    ctx.fillStyle = "#bdf3ff";
    for (let i = 0; i < 5; i++) {
      const x = (i * 240 + Math.sin(this.frame * 0.005 + i) * 40) % (WIDTH + 200) - 100;
      ctx.beginPath();
      ctx.moveTo(x, 0); ctx.lineTo(x + 80, 0);
      ctx.lineTo(x + 220, HEIGHT); ctx.lineTo(x + 60, HEIGHT);
      ctx.closePath(); ctx.fill();
    }
    ctx.restore();
  }

  drawSeabed() {
    const ctx = this.ctx;
    const y = this.screenY(COURSE_LEN - 40);
    if (y > HEIGHT) return;
    ctx.fillStyle = "#c9b27a";
    ctx.beginPath();
    ctx.moveTo(0, y + 30);
    for (let x = 0; x <= WIDTH; x += 60) ctx.lineTo(x, y + 30 - Math.sin(x * 0.03) * 12);
    ctx.lineTo(WIDTH, HEIGHT); ctx.lineTo(0, HEIGHT); ctx.closePath(); ctx.fill();
    ctx.fillStyle = "#b59b63";
    ctx.fillRect(0, Math.max(y + 40, 0), WIDTH, Math.max(0, HEIGHT - (y + 40)));
  }

  drawChest(x, y) {
    const ctx = this.ctx;
    const img = this.assets.ocean_chest;
    const pulse = 0.5 + 0.5 * Math.sin(this.frame * 0.12);
    ctx.save();
    ctx.shadowColor = `rgba(255,210,90,${0.5 + pulse * 0.4})`;
    ctx.shadowBlur = 28;
    if (ready(img)) {
      const w = 180, h = w * (img.naturalHeight / img.naturalWidth);
      ctx.drawImage(img, x - w / 2, y - h * 0.78, w, h);
    } else {
      ctx.fillStyle = "#8a5a2a"; ctx.fillRect(x - 60, y - 50, 120, 70);
      ctx.fillStyle = "#ffd24a"; ctx.fillRect(x - 50, y - 30, 100, 18);
    }
    ctx.restore();
  }

  drawCoin(x, y) {
    const ctx = this.ctx;
    const spin = Math.abs(Math.sin(this.frame * 0.12 + x * 0.05));
    const rx = 13 * (0.35 + spin * 0.65);
    ctx.save();
    ctx.shadowColor = "rgba(255,210,74,0.7)"; ctx.shadowBlur = 10;
    ctx.fillStyle = "#f2b701"; ctx.beginPath(); ctx.ellipse(x, y, rx, 14, 0, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffe17a"; ctx.beginPath(); ctx.ellipse(x, y, rx * 0.7, 10, 0, 0, Math.PI * 2); ctx.fill();
    if (rx > 7) {
      ctx.fillStyle = "#9a6b00"; ctx.font = "800 12px 'Baloo 2', system-ui, sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("C$", x, y + 1);
    }
    ctx.restore();
  }

  drawFish(f, y) {
    const ctx = this.ctx;
    const img = this.assets[f.sprite];
    ctx.save();
    ctx.translate(f.x, y);
    // sprites are drawn facing LEFT; flip when swimming right
    if (f.dir > 0) ctx.scale(-1, 1);
    if (ready(img)) {
      const h = 64 * f.scale;
      const w = h * (img.naturalWidth / img.naturalHeight);
      ctx.drawImage(img, -w / 2, -h / 2, w, h);
    } else {
      ctx.fillStyle = "#ff8a3a";
      ctx.beginPath(); ctx.ellipse(0, 0, 22 * f.scale, 13 * f.scale, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.moveTo(18 * f.scale, 0); ctx.lineTo(30 * f.scale, -10 * f.scale); ctx.lineTo(30 * f.scale, 10 * f.scale); ctx.closePath(); ctx.fill();
    }
    ctx.restore();
  }

  drawPlayer() {
    const ctx = this.ctx;
    const x = this.px, y = this.screenY(this.depth);
    const bob = Math.sin(this.frame * 0.18) * 3 + (this.wobble > 0 ? Math.sin(this.frame * 0.7) * 4 : 0);
    const tilt = Math.max(-0.4, Math.min(0.4, this.vy * 0.05));
    const img = this.assets[this.char.id];
    ctx.save();
    ctx.translate(x, y + bob);
    ctx.rotate(tilt);
    if (this.facing < 0) ctx.scale(-1, 1);
    if (this.invuln > 0 && Math.floor(this.frame / 5) % 2 === 0) ctx.globalAlpha = 0.4;
    if (ready(img)) {
      const targetH = 104;
      const scale = targetH / img.naturalHeight;
      const w = img.naturalWidth * scale;
      ctx.drawImage(img, -w / 2, -targetH / 2, w, targetH);
    } else {
      ctx.fillStyle = this.char.colors.shirt; ctx.fillRect(-12, -16, 24, 34);
      ctx.fillStyle = this.char.colors.skin; ctx.fillRect(-10, -34, 20, 18);
    }
    ctx.restore();

    // little swim bubbles trailing the player
    if (this.frame % 6 === 0) this.bubbles.push({ x: x - this.facing * 14, y: y + bob, r: 2 + Math.random() * 3, sp: 1 + Math.random(), drift: (Math.random() - 0.5) * 0.4 });
  }
}
